import { useMemo, useRef, useState, type FormEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { MEASUREMENT_TYPES, dailySeries, measurementType, monthOf, newId, targetFor, bandVerdict } from '@gk/contracts'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import { useNotebook, type MeasurementRow } from '../data/measure.ts'
import { checkForm } from '../data/measureUi.ts'
import { fmtNum } from '../lib/format.ts'
import { localDate, localToInstant } from '../lib/time.ts'
import { BackLink, Banner, DisabledHint, Field, Section, errorText } from '../ui/components.tsx'
import { useAction, useWriteAccess } from '../ui/hooks.ts'
import { RowLink } from '../ui/RowLink.tsx'
import { ReadingRow, VerdictPill, lang2, typeLabel, unitSymbol, unitsFor, useAreaOptions, usePersonNames, type AreaOption } from './measures/shared.tsx'

/** The measurement notebook (M13, ADR-100): what was measured on the course, by whom, with what and where; the club's own targets; the timeline of each place. Taking a reading is a field fact: it works with no
 *  signal and shows at once. Everything else is read from the device's own copy. */
export function MeasuresPage() {
  const { t } = useTranslation()
  const { can } = useClub()
  const nb = useNotebook()
  const areas = useAreaOptions()
  if (!can('measurement.read')) return <section className="flex flex-col gap-4" data-testid="measures-page"><h1 className="text-2xl font-bold">{t('measures.title')}</h1><Banner tone="warn"><span data-testid="measures-denied">{t('measures.denied')}</span></Banner></section>
  if (!nb.ready || !areas) return <p>{t('common.loading')}</p>
  return (
    <section className="flex flex-col gap-5" data-testid="measures-page">
      <BackLink to="/settings">← {t('common.back')}</BackLink>
      <h1 className="text-2xl font-bold">{t('measures.title')}</h1>
      <p className="meta">{t('measures.help')}</p>
      <RowLink to="/measures/daily" icon="weather" tone="water" title={t('measures.dailyGo')} testId="go-daily" />
      {/* M16-M: the timeline (rain, temperatures, humidity, soil, the meter, the runs and the observations on one axis of days) */}
      <RowLink to="/measures/timeline" icon="measure" tone="water" title={t('measures.chronoGo')} testId="go-timeline" />
      {/* M15-C: the nutrients (kg/ha) per area per season, from the declared compositions alone */}
      {can('treatment.read') && <RowLink to="/measures/fertilisation" icon="leaf" title={t('measures.fertilisationGo')} testId="go-fertilisation" />}
      <div className="grid grid-cols-2 gap-2">
        <Link to="/measures/targets" className="btn" data-testid="go-targets">{t('measures.targets')}</Link>
        {can('measurement.manage') ? <Link to="/measures/import" className="btn" data-testid="go-measure-import">{t('measures.importGo')}</Link> : <span />}
      </div>
      {can('measurement.record') && <RecordForm areas={areas} />}
      <Overview areas={areas} />
      <Recent areas={areas} />
    </section>
  )
}

// ---------------------------------------------------------------------------------------------------- taking a reading (a field fact)
function RecordForm({ areas }: { areas: AreaOption[] }) {
  const { t, i18n } = useTranslation()
  const { send, site } = useClub()
  const engine = useEngineState()
  const access = useWriteAccess('measurement.record')
  const nb = useNotebook()
  const { busy, error, run } = useAction()
  const id = useRef(newId())
  const [type, setType] = useState('green_speed')
  const [areaId, setAreaId] = useState('')
  const [value, setValue] = useState('')
  const [unit, setUnit] = useState('ft')
  const [depth, setDepth] = useState('')
  const [method, setMethod] = useState('')
  const [instrument, setInstrument] = useState('')
  const [note, setNote] = useState('')
  const [when, setWhen] = useState('')
  const [done, setDone] = useState<string | null>(null)
  const def = measurementType(type)!
  const check = checkForm({ type, value, unit, hasArea: !!areaId, depth })
  const area = areas.find((a) => a.id === areaId)
  const month = monthOf(localDate(new Date(), site.timezone))
  const target = targetFor(nb.targets, type, area?.typeCode ?? null, month)
  const verdict = check.ok && check.canonical !== undefined ? bandVerdict(Number(check.canonical), target) : null
  const past = useMemo(() => { const rows = nb.readings.filter((r) => r.measurement_type === type); return { methods: [...new Set(rows.map((r) => r.method).filter(Boolean) as string[])], instruments: [...new Set(rows.map((r) => r.instrument).filter(Boolean) as string[])] } }, [nb.readings, type])
  if (!access.permitted) return null

  function pickType(next: string) {
    setType(next); setUnit(measurementType(next)!.unit); setDone(null)
    if (!measurementType(next)!.depth) setDepth('')
    if (!measurementType(next)!.areaRequired) setAreaId((a) => a)                        // a weather reading may name a place or none
  }
  function submit(e: FormEvent) {
    e.preventDefault(); setDone(null)
    void run(async () => {
      const at = when ? localToInstant(when.slice(0, 10), when.slice(11, 16), site.timezone) : undefined
      await send('measurement.record@1', {
        measurement_id: id.current, measurement_type: type, ...(areaId ? { area_id: areaId } : {}), value: Number(value.trim().replace(',', '.')), unit,
        ...(at ? { observed_at: at } : {}), ...(depth.trim() ? { depth_mm: Number(depth) } : {}), ...(method.trim() ? { method: method.trim() } : {}),
        ...(instrument.trim() ? { instrument: instrument.trim() } : {}), ...(note.trim() ? { note: note.trim() } : {}),
      })
      id.current = newId(); setValue(''); setNote('')              // the next point of the same round: same place, type, method and instrument
      setDone(engine.online === false || !navigator.onLine ? t('measures.recordedOffline') : t('measures.recorded'))
    })
  }
  return (
    <Section title={t('measures.record')} testId="measure-record">
      <form className="card flex flex-col gap-3" onSubmit={submit} data-testid="measure-form">
        <Field label={t('measures.type')} htmlFor="m-type">
          <select id="m-type" className="input" value={type} onChange={(e) => pickType(e.target.value)} data-testid="measure-type">
            {MEASUREMENT_TYPES.map((d) => <option key={d.code} value={d.code}>{typeLabel(d, i18n.language)}</option>)}
          </select>
        </Field>
        <Field label={def.areaRequired ? t('measures.area') : t('measures.areaOptional')} htmlFor="m-area">
          <select id="m-area" className="input" value={areaId} onChange={(e) => setAreaId(e.target.value)} data-testid="measure-area">
            <option value="">{def.areaRequired ? t('mounts.choose') : t('measures.site')}</option>
            {areas.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-[1fr_auto] gap-3">
          <Field label={t('measures.value')} htmlFor="m-value">
            <input id="m-value" className="input text-xl" inputMode="decimal" autoComplete="off" value={value} onChange={(e) => { setValue(e.target.value); setDone(null) }} data-testid="measure-value" />
          </Field>
          <Field label={t('measures.unit')} htmlFor="m-unit">
            <select id="m-unit" className="input" value={unit} onChange={(e) => setUnit(e.target.value)} data-testid="measure-unit">
              {unitsFor(type).map((u) => <option key={u.code} value={u.code}>{u.symbol[lang2(i18n.language)]}</option>)}
            </select>
          </Field>
        </div>
        {check.ok && check.canonical !== undefined && (
          <p className="text-sm" data-testid="measure-preview">
            {unit !== def.unit && <span>= {fmtNum(Number(check.canonical), i18n.language, def.decimals)} {unitSymbol(def.unit, i18n.language)} · </span>}
            <VerdictPill verdict={verdict} target={target} unit={def.unit} />
          </p>
        )}
        {!check.ok && check.code && <Banner tone="warn"><span data-testid="measure-check">{errorText(t, check.code)}</span></Banner>}
        {def.depth && (
          <Field label={t('measures.depth')} hint={t('measures.depthHint')} htmlFor="m-depth">
            <input id="m-depth" className="input" inputMode="numeric" autoComplete="off" value={depth} onChange={(e) => setDepth(e.target.value)} data-testid="measure-depth" />
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('measures.method')} htmlFor="m-method"><input id="m-method" className="input" list="m-methods" maxLength={80} value={method} onChange={(e) => setMethod(e.target.value)} data-testid="measure-method" /></Field>
          <Field label={t('measures.instrument')} htmlFor="m-inst"><input id="m-inst" className="input" list="m-insts" maxLength={80} value={instrument} onChange={(e) => setInstrument(e.target.value)} data-testid="measure-instrument" /></Field>
          <datalist id="m-methods">{past.methods.map((m) => <option key={m} value={m} />)}</datalist>
          <datalist id="m-insts">{past.instruments.map((m) => <option key={m} value={m} />)}</datalist>
        </div>
        <Field label={t('measures.note')} htmlFor="m-note"><input id="m-note" className="input" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} data-testid="measure-note" /></Field>
        <Field label={t('measures.when')} hint={t('measures.whenHint')} htmlFor="m-when"><input id="m-when" className="input" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="measure-when" /></Field>
        {error && <Banner tone="error"><span data-testid="measure-error">{error}</span></Banner>}
        {done && <Banner tone="ok"><span data-testid="measure-done">{done}</span></Banner>}
        {/* M23 item 6: why "Enregistrer la mesure" is still grey, when no check above says it (an empty value, no place yet) */}
        <DisabledHint show={!check.ok && !check.code} testId="measure-save-hint">{t('measures.saveHint')}</DisabledHint>
        <button className="btn btn-primary" disabled={busy || access.readOnlyMode || !check.ok} data-testid="measure-save">{t('measures.save')}</button>
      </form>
    </Section>
  )
}

// ---------------------------------------------------------------------------------------------------- where it stands: the latest day of each place and kind
function Overview({ areas }: { areas: AreaOption[] }) {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const nb = useNotebook()
  const rows = useMemo(() => {
    const groups = new Map<string, MeasurementRow[]>()
    for (const r of nb.readings) { const k = `${r.measurement_type}|${r.area_id ?? ''}`; const a = groups.get(k); if (a) a.push(r); else groups.set(k, [r]) }
    return [...groups.entries()].map(([k, list]) => {
      const [type, areaId] = k.split('|') as [string, string]
      const def = measurementType(type)!
      const area = areas.find((a) => a.id === areaId)
      const pts = dailySeries(list)
      const last = pts[pts.length - 1]!
      const target = targetFor(nb.targets, type, area?.typeCode ?? null, monthOf(last.date))
      return { key: k, type, def, areaId, area, last, n: list.length, target, verdict: bandVerdict(last.mean, target) }
    }).sort((a, b) => a.def.sort - b.def.sort || (a.area?.label ?? '').localeCompare(b.area?.label ?? '', i18n.language, { numeric: true }))
  }, [nb.readings, nb.targets, areas, i18n.language])
  void site
  return (
    <Section title={t('measures.overview')} help={t('measures.overviewHelp')} testId="measure-overview">
      {rows.length === 0 && <p className="opacity-70" data-testid="measure-none">{t('measures.none')}</p>}
      <ul className="flex flex-col gap-2">
        {rows.map((r) => (
          <li key={r.key} data-testid="overview-row" data-type={r.type} data-area={r.areaId}>
            <Link to="/measures/series" search={{ type: r.type, ...(r.areaId ? { area: r.areaId } : {}) }} className="card flex flex-col gap-1 no-underline">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-semibold">{typeLabel(r.def, i18n.language)} · {r.area?.label ?? t('measures.site')}</span>
                <span className="text-xl font-bold">{fmtNum(r.last.mean, i18n.language, r.def.decimals)} {unitSymbol(r.def.unit, i18n.language)}</span>
              </div>
              <p className="meta flex flex-wrap items-center gap-2">
                <span>{t('measures.lastDay', { date: r.last.date.split('-').reverse().join('/'), count: r.last.n })}</span>
                <span>· {t('measures.readings', { count: r.n })}</span>
                <VerdictPill verdict={r.verdict} target={r.target} unit={r.def.unit} />
              </p>
            </Link>
          </li>
        ))}
      </ul>
    </Section>
  )
}

// ---------------------------------------------------------------------------------------------------- the latest readings
function Recent({ areas }: { areas: AreaOption[] }) {
  const { t, i18n } = useTranslation()
  const nb = useNotebook()
  const nameOf = usePersonNames()
  const [type, setType] = useState('')
  const [areaId, setAreaId] = useState('')
  const [limit, setLimit] = useState(20)
  const list = useMemo(() => nb.readings.filter((r) => (!type || r.measurement_type === type) && (!areaId || r.area_id === areaId))
    .sort((a, b) => (a.observed_at < b.observed_at ? 1 : a.observed_at > b.observed_at ? -1 : a.id < b.id ? 1 : -1)), [nb.readings, type, areaId])
  return (
    <Section title={t('measures.recent')} testId="measure-recent">
      <div className="grid grid-cols-2 gap-3">
        <select className="input" aria-label={t('measures.type')} value={type} onChange={(e) => setType(e.target.value)} data-testid="recent-type">
          <option value="">{t('measures.allTypes')}</option>
          {MEASUREMENT_TYPES.map((d) => <option key={d.code} value={d.code}>{typeLabel(d, i18n.language)}</option>)}
        </select>
        <select className="input" aria-label={t('measures.area')} value={areaId} onChange={(e) => setAreaId(e.target.value)} data-testid="recent-area">
          <option value="">{t('measures.allAreas')}</option>
          {areas.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
        </select>
      </div>
      <ul className="flex flex-col gap-2" data-testid="recent-list">
        {list.slice(0, limit).map((r) => <ReadingRow key={r.id} r={r} areaName={areas.find((a) => a.id === r.area_id)?.label ?? null} who={nameOf(r.actor_person_id)} />)}
      </ul>
      {list.length > limit && <button className="btn" onClick={() => setLimit((n) => n + 30)} data-testid="recent-more">{t('measures.showMore')}</button>}
    </Section>
  )
}
